import type { FC, ReactNode } from 'react'
import type { EventCard as EventCardData } from '@/types'

import Link from 'next/link'
import { Globe, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms, formatPrice } from '@/lib/format'
import { Badge } from '@/components/ui/badge'
import { CategoryIcon } from '@/components/events/category-icon'

interface EventCardProps {
    event: EventCardData
    className?: string
    showSummary?: boolean
}

export const getAvailability = (event: Pick<EventCardData, 'capacity' | 'taken'>): 'sold-out' | 'few-left' | 'available' => {
    if (event.capacity <= 0) return 'available'
    const remaining = event.capacity - event.taken
    if (remaining <= 0) return 'sold-out'
    if (remaining <= Math.max(5, Math.round(event.capacity * 0.1))) return 'few-left'
    return 'available'
}

export const EventCard: FC<EventCardProps> = ({ event, className, showSummary = false }): ReactNode => {
    const availability = getAvailability(event)

    return (
        <article
            className={cn(
                'group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-ink/5',
                className
            )}
        >
            <div className='relative aspect-[16/10] overflow-hidden bg-muted'>
                {event.coverImage
                    ? (
                        <img
                            src={event.coverImage}
                            alt={event.coverImageAlt ?? ''}
                            loading='lazy'
                            className='size-full object-cover transition duration-500 group-hover:scale-105'
                        />
                    )
                    : (
                        <div className='flex size-full items-center justify-center bg-gradient-to-br from-primary/90 to-ink'>
                            <CategoryIcon slug={event.category?.slug} className='size-12 text-white/70' />
                        </div>
                    )}

                <div className='absolute left-4 top-4 flex min-w-14 flex-col items-center rounded-2xl bg-card/95 px-3 py-2 text-center shadow-md backdrop-blur'>
                    <span className='text-[11px] font-bold tracking-[0.14em] text-primary'>
                        {event.month}
                    </span>
                    <span className='font-heading text-2xl font-extrabold leading-none'>
                        {event.day}
                    </span>
                </div>

                {availability !== 'available' && (
                    <div className='absolute right-4 top-4'>
                        <Badge variant={availability === 'sold-out' ? 'ink' : 'accent'} className='shadow'>
                            {availability === 'sold-out' ? 'Sold out' : 'Few left'}
                        </Badge>
                    </div>
                )}
            </div>

            <div className='flex flex-1 flex-col gap-3 p-5'>
                <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-muted-foreground'>
                    {event.category && (
                        <span className='inline-flex items-center gap-1.5 text-primary'>
                            <CategoryIcon slug={event.category.slug} className='size-3.5' />
                            <span data-mdlfy-cms={cms('categories', event.category.id, 'name')}>{event.category.name}</span>
                        </span>
                    )}

                    <span>
                        {event.shortDateLabel} at {event.timeLabel}
                    </span>
                </div>

                <h3 className='text-lg font-bold leading-snug'>
                    <Link
                        href={`/events/${event.slug}`}
                        className='rounded after:absolute after:inset-0 after:content-[""] focus-visible:outline-none focus-visible:after:rounded-3xl focus-visible:after:ring-2 focus-visible:after:ring-ring'
                    >
                        <span data-mdlfy-cms={cms('events', event.id, 'title')}>{event.title}</span>
                    </Link>
                </h3>

                {showSummary && event.summary && (
                    <p className='line-clamp-2 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={cms('events', event.id, 'summary')}>
                        {event.summary}
                    </p>
                )}

                <p className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                    {event.isOnline
                        ? (
                            <>
                                <Globe className='size-4 shrink-0' aria-hidden='true' />
                                <span>Online event</span>
                            </>
                        )
                        : (
                            <>
                                <MapPin className='size-4 shrink-0' aria-hidden='true' />
                                <span className='truncate'>
                                    {event.venueName && <span data-mdlfy-cms={cms('events', event.id, 'venue_name')}>{event.venueName}</span>}
                                    {event.venueName && event.city && <span>, </span>}
                                    {event.city && <span data-mdlfy-cms={cms('events', event.id, 'city')}>{event.city}</span>}
                                </span>
                            </>
                        )}
                </p>

                <div className='mt-auto flex items-center justify-between gap-3 border-t border-dashed border-border pt-4'>
                    <span className='text-sm font-bold'>
                        {event.minPrice === null
                            ? 'Tickets coming soon'
                            : event.minPrice === 0 ? 'Free' : `From ${formatPrice(event.minPrice, event.currency)}`}
                    </span>

                    <span className='truncate text-xs text-muted-foreground'>
                        by <span data-mdlfy-cms={cms('organizers', event.organizer.id, 'name')}>{event.organizer.name}</span>
                    </span>
                </div>
            </div>
        </article>
    )
}
